import { useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Card, Heading, Input } from '@openroad/ui';
import { safeNext } from '../lib/navigation';
import { useSession } from '../lib/session';

/**
 * The server's own minimum, repeated here so the requirement is visible before
 * the form is submitted rather than arriving as a 400 afterwards. It is a hint,
 * not a second validator — `/auth/register` remains the one that decides.
 */
const PASSWORD_MIN = 10;

export function RegisterScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register, member } = useSession();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const next = safeNext(params.get('next'));

  // Same reasoning as the sign-in screen: a redirect as a render result rather
  // than a side effect React would run twice in strict mode.
  if (member) return <Navigate to={next} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register({ firstName, lastName, email, password });
      navigate(next, { replace: true });
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'We could not create your account. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="mx-auto max-w-[440px] px-6 pt-14 pb-16">
      <Card radius="panel" className="p-8">
        <Heading level="panel" as="h1" className="mb-1.5">
          Create your account
        </Heading>
        <p className="m-0 mb-6 text-base text-muted">
          Book a seat, offer one, and keep your saved CO₂ adding up.
        </p>

        <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
          <div className="flex gap-3">
            <Input
              label="First name"
              autoComplete="given-name"
              required
              className="w-full"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
            <Input
              label="Last name"
              autoComplete="family-name"
              required
              className="w-full"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>
          <Input
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Input
            label="Password"
            type="password"
            autoComplete="new-password"
            required
            hint={`At least ${PASSWORD_MIN} characters.`}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          {error ? (
            <p
              role="alert"
              className="m-0 rounded-lg bg-pohutukawa-tint px-3.5 py-3 text-label text-danger"
            >
              {error}
            </p>
          ) : null}

          <Button type="submit" variant="cta" size="hero" block disabled={submitting}>
            {submitting ? 'Creating your account…' : 'Create account'}
          </Button>
        </form>

        <p className="mt-6 mb-0 text-meta text-subtle">
          Already have an account?{' '}
          <Link to={`/sign-in?next=${encodeURIComponent(next)}`} className="text-ink underline">
            Sign in
          </Link>
          .
        </p>
      </Card>
    </main>
  );
}
